import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { supabase } from '../../supabaseClient'

function AddVariants() {
  const { lureId } = useParams()
  const navigate = useNavigate()

  const [lure, setLure] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  // Color entries: array of { colorName, isLimitedEdition }
  const [colorEntries, setColorEntries] = useState([{ colorName: '', isLimitedEdition: false, intendedUse: 'both' }])

  useEffect(() => {
    fetchLure()
  }, [lureId])

  const fetchLure = async () => {
    try {
      const { data, error } = await supabase
        .from('base_lures')
        .select('*, brands (brand_id, brand_name)')
        .eq('base_lure_id', lureId)
        .single()

      if (error) throw error

      setLure(data)
      setLoading(false)
    } catch (err) {
      console.error('Error fetching lure:', err)
      setError('Failed to load lure')
      setLoading(false)
    }
  }

  const addColorEntry = () => {
    setColorEntries(prev => [...prev, { colorName: '', isLimitedEdition: false, intendedUse: 'both' }])
  }

  const removeColorEntry = (index) => {
    setColorEntries(prev => prev.filter((_, i) => i !== index))
  }

  const updateColorEntry = (index, field, value) => {
    setColorEntries(prev => prev.map((entry, i) =>
      i === index ? { ...entry, [field]: value } : entry
    ))
  }

  const handleSubmit = async () => {
    const validEntries = colorEntries.filter(e => e.colorName.trim())
    if (validEntries.length === 0) {
      setError('Please enter at least one color name')
      return
    }

    setSubmitting(true)
    setError('')

    try {
      const variants = validEntries.map(entry => ({
        base_lure_id: parseInt(lureId),
        color_name: entry.colorName.trim(),
        is_limited_edition: entry.isLimitedEdition,
        intended_use: entry.intendedUse || 'both'
      }))

      const { error: insertError } = await supabase
        .from('lure_variants')
        .insert(variants)

      if (insertError) throw insertError

      setSuccess(true)
      setTimeout(() => navigate(`/lure/${lureId}`), 2000)

    } catch (err) {
      console.error('Error creating variants:', err)
      if (err.code === '23505') {
        setError('One or more of these color variants already exists for this lure.')
      } else {
        setError(err.message || 'Failed to create variants')
      }
      setSubmitting(false)
    }
  }

  if (loading) {
    return <div className="loading">Loading...</div>
  }

  if (!lure) {
    return <div className="error-message">Lure not found</div>
  }

  if (success) {
    return (
      <div className="admin-success">
        <h1>✅ Variants Created Successfully!</h1>
        <p>Redirecting to lure page...</p>
      </div>
    )
  }

  return (
    <div className="admin-form-page">
      <h1>Add Color Variants</h1>
      <div className="lure-info-banner">
        <strong>{lure.name}</strong> by {lure.brands?.brand_name}
      </div>

      {error && <div className="error-message">❌ {error}</div>}

      <p style={{ marginBottom: '1rem', color: '#475569' }}>
        Enter each color name exactly as the manufacturer prints it (e.g., "Sexy Shad", "Ghost Minnow").
      </p>

      <div className="color-entries-list">
        {colorEntries.map((entry, index) => (
          <div key={index} className="color-entry-row">
            <div className="color-entry-inputs">
              <input
                type="text"
                value={entry.colorName}
                onChange={(e) => updateColorEntry(index, 'colorName', e.target.value)}
                placeholder={`Color name #${index + 1} (e.g., Sexy Shad)`}
                className="form-input"
              />
              <label className="checkbox-label-admin" style={{ whiteSpace: 'nowrap' }}>
                <input
                  type="checkbox"
                  checked={entry.isLimitedEdition}
                  onChange={(e) => updateColorEntry(index, 'isLimitedEdition', e.target.checked)}
                />
                <span>Limited Edition</span>
              </label>
              <select
                value={entry.intendedUse || 'both'}
                onChange={(e) => updateColorEntry(index, 'intendedUse', e.target.value)}
                className="form-select"
                style={{ minWidth: '140px' }}
              >
                <option value="both">Fishing & Collectible</option>
                <option value="collection">Collectible Only</option>
                <option value="fishing">Fishing Only</option>
              </select>
              {colorEntries.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeColorEntry(index)}
                  className="btn-delete-small"
                  title="Remove this entry"
                >
                  🗑️
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <button type="button" onClick={addColorEntry} className="btn-secondary" style={{ marginTop: '0.5rem' }}>
        + Add Another Color
      </button>

      <div className="form-actions" style={{ marginTop: '1.5rem' }}>
        <Link to="/admin" className="btn-secondary">Cancel</Link>
        <button
          onClick={handleSubmit}
          className="btn-primary"
          disabled={submitting || colorEntries.every(e => !e.colorName.trim())}
        >
          {submitting
            ? 'Creating Variants...'
            : `Create ${colorEntries.filter(e => e.colorName.trim()).length} Variant${colorEntries.filter(e => e.colorName.trim()).length !== 1 ? 's' : ''}`
          }
        </button>
      </div>
    </div>
  )
}

export default AddVariants
